import { type PropsWithChildren } from 'react'
import { useTheme } from '../../../shared/hooks/useTheme'
import { cn } from '@/lib/utils'
import { getThemeColor } from '@/shared/hooks/useThemeColors'
import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'
import { useMouseSpotlight } from './useMouseSpotlight'

export function LandingFrame({ children }: PropsWithChildren) {
  const { theme } = useTheme()
  const { ref: spotlightRef } = useMouseSpotlight<HTMLDivElement>()

  return (
    <div
      ref={spotlightRef}
      className="relative min-h-screen overflow-x-hidden bg-background scroll-smooth"
    >
      {/* Background layer: grid + glow orbs + spotlight */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        {/* Subtle dot-grid */}
        <div
          className={cn(
            'absolute inset-0',
            'bg-[linear-gradient(to_right,var(--landing-grid-color)_1px,transparent_1px),linear-gradient(to_bottom,var(--landing-grid-color)_1px,transparent_1px)]',
            'bg-[size:64px_64px]',
            theme === 'dark' ? 'opacity-[0.04]' : 'opacity-[0.10]',
          )}
          style={
            {
              '--landing-grid-color': getThemeColor('--glow-grid'),
            } as React.CSSProperties
          }
        />

        {/* Top-center glow — indigo */}
        <div className="absolute -top-32 left-1/2 h-[480px] w-[600px] -translate-x-1/2 rounded-full blur-[120px] landing-glow-a bg-[var(--glow-brand-strong)]" />

        {/* Left glow — violet */}
        <div className="absolute top-[30%] -left-20 h-[360px] w-[360px] rounded-full blur-[100px] landing-glow-b bg-[var(--glow-secondary)]" />

        {/* Right glow — indigo/slate */}
        <div className="absolute right-[-4%] top-[55%] h-[400px] w-[400px] rounded-full blur-[100px] landing-glow-c bg-[var(--glow-brand)]" />

        {/* Mouse spotlight — dark mode only */}
        {theme === 'dark' && <div className="spotlight-overlay absolute inset-0" />}
      </div>

      {/* Content */}
      <div className="relative z-10">
        <LandingHeader />
        <main className="pt-16">{children}</main>
        <LandingFooter />
      </div>
    </div>
  )
}
